import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Zap, Headphones, Sparkles, MapPin } from "lucide-react";
import EventCard from "@/components/EventCard";
import { fetchEvents, joinWaitlist, waitlistCount } from "@/lib/api";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/e18f10b8-c864-48c7-b293-ba205b8f8ab6/images/5aab30743861b3a38a453946676cbf1f09740e190d434fa852072cb4d885557d.png";

const marqueeItems = ["New York", "Los Angeles", "Miami", "Chicago", "Austin", "Las Vegas", "Brooklyn", "Nashville", "Seattle", "Atlanta"];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    fetchEvents({ featured: true }).then(setFeatured).catch(() => {});
    waitlistCount().then((d) => setCount(d.count)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await joinWaitlist(email);
      toast.success("You're on the list. See you at the door.");
      setEmail("");
      const { count: c } = await waitlistCount();
      setCount(c);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join waitlist");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="home-page">
      {/* ===== HERO ===== */}
      <section
        data-testid="hero-section"
        className="relative min-h-[92vh] flex items-center overflow-hidden grain"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/40 via-[#0A0A0A]/60 to-[#0A0A0A]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(180,255,57,0.18),transparent_60%)]" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 space-y-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 bg-white/5 backdrop-blur-md">
                <span className="w-1.5 h-1.5 bg-[#B4FF39] live-dot" />
                <span className="text-[10px] tracking-[0.3em] uppercase font-bold">
                  Coming Soon · Spring 2026
                </span>
              </div>

              <h1
                data-testid="hero-headline"
                className="font-display text-[3rem] leading-[0.92] sm:text-6xl md:text-7xl lg:text-[7.5rem] uppercase tracking-[-0.04em]"
              >
                Tonight<br />
                <span className="text-[#B4FF39]">never</span> ends<br />
                in <span className="italic font-light tracking-tighter">365</span>cities.
              </h1>

              <p className="max-w-xl text-base md:text-lg text-zinc-300 leading-relaxed">
                365LIVE is the events marketplace tuned for the after-hours
                economy. Festivals, nightlife, art, tech — everything happening
                tonight, bookable in two taps.
              </p>

              <form
                onSubmit={handleSubmit}
                data-testid="waitlist-form"
                className="flex flex-col sm:flex-row gap-2 sm:gap-0 max-w-xl"
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="waitlist-email-input"
                  className="flex-1 bg-white/5 border border-white/15 text-white px-5 py-4 focus:border-[#B4FF39] outline-none transition-colors backdrop-blur-sm placeholder:text-zinc-500 font-mono-tight text-sm"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="waitlist-submit-button"
                  className="bg-[#B4FF39] text-[#0A0A0A] font-bold px-6 py-4 hover:bg-[#9FE822] transition-colors uppercase tracking-[0.15em] text-xs flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting ? "..." : "Join Waitlist"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>

              <div className="flex items-center gap-6 text-xs text-zinc-400 uppercase tracking-[0.2em]">
                <span data-testid="waitlist-count" className="font-mono-tight">
                  <span className="text-white font-bold">{count.toLocaleString()}</span> joined
                </span>
                <span className="w-px h-3 bg-white/20" />
                <span>No spam · Unsubscribe anytime</span>
              </div>
            </div>

            <div className="lg:col-span-4 space-y-4">
              <div className="border border-white/10 bg-white/[0.02] backdrop-blur-md p-6">
                <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">Now Live</p>
                {featured[0] && (
                  <Link to={`/events/${featured[0].slug}`} className="block group">
                    <p className="font-display text-2xl leading-tight mb-2 group-hover:text-[#B4FF39] transition-colors">
                      {featured[0].title}
                    </p>
                    <p className="text-xs text-zinc-400 flex items-center gap-2">
                      <MapPin className="w-3 h-3" />
                      {featured[0].city} · {featured[0].venue}
                    </p>
                  </Link>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="border border-white/10 p-4">
                  <Zap className="w-4 h-4 text-[#B4FF39] mb-2" />
                  <p className="font-display text-2xl">2T</p>
                  <p className="text-[9px] tracking-[0.2em] uppercase text-zinc-500">Tap Book</p>
                </div>
                <div className="border border-white/10 p-4">
                  <Headphones className="w-4 h-4 text-[#B4FF39] mb-2" />
                  <p className="font-display text-2xl">24/7</p>
                  <p className="text-[9px] tracking-[0.2em] uppercase text-zinc-500">Live</p>
                </div>
                <div className="border border-white/10 p-4">
                  <Sparkles className="w-4 h-4 text-[#B4FF39] mb-2" />
                  <p className="font-display text-2xl">0%</p>
                  <p className="text-[9px] tracking-[0.2em] uppercase text-zinc-500">Fee*</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== City Marquee ===== */}
      <section className="border-y border-white/5 overflow-hidden bg-[#0A0A0A]">
        <div className="flex marquee-track py-6 whitespace-nowrap">
          {[...marqueeItems, ...marqueeItems].map((c, i) => (
            <div key={i} className="flex items-center gap-6 px-8 text-xs tracking-[0.4em] uppercase text-zinc-500">
              <span className="text-[#B4FF39]">◆</span>
              <span>{c}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Featured Events ===== */}
      <section data-testid="featured-section" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="flex items-end justify-between mb-12 gap-6">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">
              ◆ Featured This Week
            </p>
            <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.95]">
              The Highlight<br />Reel.
            </h2>
          </div>
          <Link
            to="/events"
            data-testid="view-all-events"
            className="hidden md:inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-zinc-300 hover:text-[#B4FF39] link-underline"
          >
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.slice(0, 6).map((e, i) => (
            <EventCard key={e.id} event={e} index={i} />
          ))}
        </div>

        <Link
          to="/events"
          className="mt-10 md:hidden inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#B4FF39]"
        >
          View All <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      {/* ===== How It Works ===== */}
      <section className="bg-[#121212] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">◆ How It Works</p>
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter mb-16 max-w-3xl leading-[0.95]">
            Less friction.<br />More <span className="text-[#B4FF39]">night</span>.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {[
              { n: "01", t: "Discover", d: "Browse curated events from the cities you love. Filter by vibe, not just by date." },
              { n: "02", t: "Reserve", d: "Two-tap RSVP. No credit card needed for the MVP — just your name and email." },
              { n: "03", t: "Show Up", d: "Get a confirmation code. Flash it at the door. Disappear into the night." },
            ].map((s) => (
              <div key={s.n} className="bg-[#121212] p-8 md:p-10">
                <p className="font-mono-tight text-xs text-[#B4FF39] mb-6">{s.n}</p>
                <h3 className="font-display text-3xl uppercase tracking-tight mb-3">{s.t}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Waitlist CTA bottom ===== */}
      <section id="waitlist" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="border border-white/10 bg-gradient-to-br from-white/[0.02] to-transparent p-10 md:p-16 grain relative overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center relative">
            <div className="md:col-span-7">
              <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.95]">
                Get in early.<br />
                <span className="text-[#B4FF39]">Stay up late.</span>
              </h2>
              <p className="mt-5 text-zinc-400 max-w-md">
                Join the waitlist for early access, founder drops, and invite-only nights in your city.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="md:col-span-5 flex flex-col sm:flex-row gap-2 sm:gap-0">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="waitlist-email-input-bottom"
                className="flex-1 bg-white/5 border border-white/15 text-white px-5 py-4 focus:border-[#B4FF39] outline-none placeholder:text-zinc-500 font-mono-tight text-sm"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="waitlist-submit-button-bottom"
                className="bg-[#B4FF39] text-[#0A0A0A] font-bold px-6 py-4 hover:bg-[#9FE822] transition-colors uppercase tracking-[0.15em] text-xs flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting ? "..." : "Notify Me"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
